import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { UpsertFixtureRosterModel } from "~pages/Roster/FixtureRosterListPage/types/upsertFixtureRosterModel.ts";
import { useDeleteRosterPlayer } from "~pages/Roster/hooks/useDeleteRosterPlayer.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import type { RosterResponse } from "~types/roster.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useExportFixtureRoster } from "./useExportFixtureRoster.ts";
import { useImportFixtureRoster } from "./useImportFixtureRoster.tsx";
import useUpsertFixtureRosterPlayer from "./useUpsertFixtureRosterPlayer.ts";

const isRosterUpsertSucceeded = (response: RosterResponse) => Boolean(response?.data?.length);

export const useFixtureRosterService = (entityId: string, fixtureId: string) => {
  const { mutateAsync: upsertFixtureRosterPlayerFn, isPending: isUpsertPending } = useUpsertFixtureRosterPlayer();
  const { mutateAsync: deleteRosterPlayerFn, isPending: isDeletePending } = useDeleteRosterPlayer();
  const { mutateAsync: importFixtureRosterFn, isPending: isImportPending } = useImportFixtureRoster(entityId, fixtureId);
  const { mutateAsync: exportFixtureRosterFn, isPending: isExportPending } = useExportFixtureRoster(entityId, fixtureId);

  const { update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.FIXTURE_ROSTER);

  const upsertFixtureRosterPlayer = (payload: UpsertFixtureRosterModel, successMessageId: string) =>
    update(() => upsertFixtureRosterPlayerFn(payload), {
      successMessageId,
      isSucceeded: isRosterUpsertSucceeded,
    });

  const deleteFixtureRosterPlayer = async (personId: string) =>
    Boolean(
      await remove(() => deleteRosterPlayerFn(personId), {
        successMessageId: "fixture.roster.deleted.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  const importFixtureRoster = async (fileName: string) =>
    executeOrThrow(() => importFixtureRosterFn(fileName), {
      successMessageId: "fixtureRoster.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportFixtureRoster = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportFixtureRosterFn(fieldNames), {
      successMessageId: "fixtureRoster.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    upsertFixtureRosterPlayer,
    isUpsertPending,
    deleteFixtureRosterPlayer,
    isDeletePending,
    importFixtureRoster,
    isImportPending,
    exportFixtureRoster,
    isExportPending,
  };
};
